import { ref, onBeforeUnmount } from '@vue/composition-api';
import { message } from '@utils/wuji-message';
import wujiFetch from '@utils/wujiFetch';
import logger from '@utils/logger';
import { BASE_API_PATH, HAS_OTHER_AUTH_SERVICE } from '@/config/constant';
export var NewStatus;
(function (NewStatus) {
    NewStatus[NewStatus["APPROVING"] = 0] = "APPROVING";
    NewStatus[NewStatus["APPROVED"] = 1] = "APPROVED";
    NewStatus[NewStatus["REJECTED"] = 2] = "REJECTED";
})(NewStatus || (NewStatus = {}));
;
export var LoginType;
(function (LoginType) {
    LoginType[LoginType["IOA"] = 0] = "IOA";
    LoginType[LoginType["QQ"] = 1] = "QQ";
})(LoginType || (LoginType = {}));
export var AuthPlatformType;
(function (AuthPlatformType) {
    AuthPlatformType["DEFAULT"] = "default";
    AuthPlatformType["PANGU"] = "pangu";
    AuthPlatformType["POWER"] = "power";
    // 模拟实现第三方鉴权，存入 db 时还是 default 值
    AuthPlatformType["THIRD"] = "third";
})(AuthPlatformType || (AuthPlatformType = {}));
const url = `${BASE_API_PATH}/xy/setting/auth`;
const map = new Map();
export const fetchAuthSetting = async (projectId, useCache = false) => {
    if (!HAS_OTHER_AUTH_SERVICE)
        return null;
    let promise = null;
    if (map.has(projectId) && useCache) {
        promise = map.get(projectId);
    }
    else {
        promise = wujiFetch(`${url}?projectid=${projectId}`);
        map.set(projectId, promise);
    }
    const data = await promise;
    return data;
};
// 轮询盘古系统状态
export const updateFetchAuthSetting = async (projectId) => {
    const result = await wujiFetch(`${url}?projectid=${projectId}`, {
        method: 'PUT',
    });
    return result;
};
export const resetAuthSetting = async (projectId, envId) => {
    const data = await wujiFetch(`${url}/${envId}?projectid=${projectId}`, { method: 'DELETE' });
    return data;
};
export const initAuthEnvProject = async (projectId, envId) => {
    const data = await wujiFetch(`${url}/${envId}/init?projectid=${projectId}`, { method: 'POST', timeout: 60000 });
    return data;
};
export const migratePanguProject = async (projectId, envId) => {
    const data = await wujiFetch(`${url}/${envId}/migration?projectid=${projectId}`, { method: 'POST', timeout: 60000 });
    return data;
};
;
export const generateAuthEnv = async (projectId, envId, params) => {
    await wujiFetch(`${url}/${envId}?projectid=${projectId}`, { method: 'POST', body: JSON.stringify(params) });
};
export const patchAuthEnv = async (projectId, envId, data) => {
    const result = await wujiFetch(`${url}/${envId}?projectid=${projectId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    });
    return result;
};
export const saveAuthSetting = async (projectId, data) => {
    const result = await wujiFetch(`${url}?projectid=${projectId}`, {
        method: 'POST',
        body: JSON.stringify(data),
    });
    return result;
};
export const useAuthSetting = (projectId) => {
    const authSetting = ref(null);
    const saving = ref(false);
    const task = ref(0);
    const fetchProjectAuthSetting = async (needSetup = true) => {
        try {
            authSetting.value = (await fetchAuthSetting(projectId)) || { authPlatform: AuthPlatformType.DEFAULT, mode: 'independent', envMapping: null };
            if (needSetup)
                setupUpdateTask(true);
        }
        catch (error) {
            logger.error('获取鉴权设置失败', error);
            message.error('获取鉴权设置失败');
        }
    };
    const updateFetchProjectAuthSetting = async () => {
        try {
            const result = await updateFetchAuthSetting(projectId);
            if (!result)
                return;
            authSetting.value = result;
            setupUpdateTask();
        }
        catch (error) {
            logger.error('查询盘古系统状态失败', error);
            message.error('查询盘古系统状态失败');
        }
    };
    const clearTask = () => clearTimeout(task.value);
    onBeforeUnmount(() => clearTask());
    const setupUpdateTask = (firstTime = false) => {
        const envMapping = authSetting.value?.envMapping;
        let needTaskToUpdate = false;
        if (envMapping) {
            for (const envInfo of Object.values(envMapping)) {
                if (envInfo.panguId && envInfo.newStatus === NewStatus.APPROVING) {
                    needTaskToUpdate = true;
                }
            }
        }
        if (needTaskToUpdate) {
            clearTask();
            task.value = window.setTimeout(() => {
                (async () => await updateFetchProjectAuthSetting())();
            }, firstTime ? 1000 : 15000);
        }
    };
    const saveProjectAuthSetting = async (data, notifySuccessMsg = true) => {
        saving.value = true;
        try {
            await saveAuthSetting(projectId, data);
            notifySuccessMsg && message.success('已保存');
            return true;
        }
        catch (error) {
            logger.error('保存鉴权设置失败', error);
            message.error('保存鉴权设置失败');
        }
        finally {
            saving.value = false;
        }
        return false;
    };
    const createAuthEnv = async (envId, authPlatform = AuthPlatformType.PANGU, { host = '', serviceHost = '' }) => {
        try {
            await generateAuthEnv(projectId, envId, { authPlatform, host, serviceHost });
            message.success('已生成');
            return true;
        }
        catch (error) {
            logger.error('生成系统失败', error);
            message.error(`生成系统失败: ${error.message}`);
        }
    };
    const resetEnvAuthSetting = async (envId) => {
        try {
            await resetAuthSetting(projectId, envId);
            message.success('重置环境成功');
            return true;
        }
        catch (error) {
            logger.error('重置环境失败', error);
            message.error('重置环境失败');
        }
    };
    const manualSetAuthEnv = async (envId, data, authPlatform = AuthPlatformType.PANGU) => {
        try {
            await patchAuthEnv(projectId, envId, { envMapping: { [envId]: data }, authPlatform });
            message.success('自行关联成功');
            return true;
        }
        catch (error) {
            logger.error('自行关联失败', error);
            message.error('自行关联失败');
        }
        return false;
    };
    const markAsSuccessPanguProject = async (envId, authPlatform = AuthPlatformType.PANGU) => {
        try {
            await patchAuthEnv(projectId, envId, { authPlatform, envMapping: { [envId]: { migrationStatus: 'success' } } });
            message.success('已设置迁移成功');
            return true;
        }
        catch (error) {
            logger.error('设置迁移状态失败', error);
            message.error('设置迁移状态失败');
        }
    };
    const updatePrefix = async (envId, prefix, authPlatform = AuthPlatformType.PANGU) => {
        try {
            await patchAuthEnv(projectId, envId, { envMapping: { [envId]: { prefix } }, authPlatform });
            message.success('已更新前缀');
            return true;
        }
        catch (error) {
            logger.error('更新前缀失败', error);
            message.error('更新前缀失败');
        }
    };
    const enabledPanguProject = async (envId, authPlatform = AuthPlatformType.PANGU) => {
        try {
            await patchAuthEnv(projectId, envId, { envMapping: { [envId]: { enabled: true } }, authPlatform });
            message.success('已启用');
            return true;
        }
        catch (error) {
            logger.error('启用系统失败', error);
            message.error('启用系统失败');
        }
    };
    const disabledPanguProject = async (envId, authPlatform = AuthPlatformType.PANGU) => {
        try {
            await patchAuthEnv(projectId, envId, { envMapping: { [envId]: { enabled: false } }, authPlatform });
            message.success('已禁用');
            return true;
        }
        catch (error) {
            logger.error('禁用系统失败', error);
            message.error('禁用系统失败');
        }
    };
    const initialize = async (envId) => {
        try {
            const resp = await initAuthEnvProject(projectId, envId);
            authSetting.value = resp.authSetting;
            if (resp.result) {
                message.success('已初始化');
                return true;
            }
            message.warn(`初始化系统失败: ${resp.message}`);
        }
        catch (error) {
            logger.error('初始化系统失败', error);
            message.error(`初始化系统失败: ${error.message}`);
        }
    };
    const migrate = async (envId) => {
        try {
            const resp = await migratePanguProject(projectId, envId);
            if (!resp.hasError) {
                await fetchProjectAuthSetting(false);
                message.success('迁移成功');
                return null;
            }
            message.warn('迁移数据失败');
            return resp;
        }
        catch (error) {
            logger.error('迁移数据失败', error);
            message.error('迁移数据失败');
            return null;
        }
    };
    return {
        authSetting,
        fetchProjectAuthSetting,
        saveProjectAuthSetting,
        newPanguProject: createAuthEnv,
        createAuthEnv,
        saving,
        initialize,
        migrate,
        manualSetPanguProject: manualSetAuthEnv,
        manualSetAuthEnv,
        markAsSuccessPanguProject,
        updatePrefix,
        enabledPanguProject,
        disabledPanguProject,
        resetEnvAuthSetting,
    };
};
